import type {InjectionKey} from 'vue'
import {useApi} from '~/composables/useApi'

export function useTelegramNotifications() {
    const {apiQuery} = useApi()

    const isLoading = ref(false)
    const isSubscribed = ref(false)
    const code = ref<string | null>(null)
    const botUsername = ref<string | null>(null)
    const errorMessage = ref<string | null>(null)

    const botLink = computed(() => {
        if (!botUsername.value || !code.value) return '#'
        return `https://t.me/${botUsername.value}?start=subscribe_${code.value}`
    })

    const resetError = () => {
        errorMessage.value = null
    }

    const checkSubscription = async () => {
        try {
            resetError()
            const response =
                await apiQuery<{ data: { isSubscribed: boolean } }>(`/auth/check-telegram`, 'GET')

            isSubscribed.value = response.data.isSubscribed
        } catch (e) {
            console.error('Ошибка при проверке подписки:', e);
            errorMessage.value = 'Не удалось проверить статус подписки. Попробуйте позже.'
        }
    }

    const handleGenerateCode = async () => {
        try {
            resetError()
            isLoading.value = true
            const response = await apiQuery<{
                data: { code: string; botUsername: string }
            }>(`/auth/generate-telegram-code`, 'GET')
            code.value = response.data.code
            botUsername.value = response.data.botUsername

            // Автоматически открыть Telegram после генерации кода
            const link = `https://t.me/${response.data.botUsername}?start=subscribe_${response.data.code}`
            window.open(link, '_blank')
        } catch (e) {
            console.error('Ошибка при генерации кода:', e);
            errorMessage.value = 'Не удалось сгенерировать ссылку. Попробуйте ещё раз.'
        } finally {
            isLoading.value = false
        }
    }

    const handleUnsubscribe = async () => {
        try {
            resetError()
            isLoading.value = true
            await apiQuery('/auth/unsubscribe-telegram', 'POST')
            isSubscribed.value = false
            code.value = null
        } catch (e) {
            console.error('Ошибка при отписке:', e);
            errorMessage.value = 'Не удалось отписаться. Попробуйте ещё раз.'
        } finally {
            isLoading.value = false
        }
    }

    onMounted(checkSubscription)

    return {
        isLoading,
        isSubscribed,
        code,
        botUsername,
        botLink,
        errorMessage,

        checkSubscription,
        handleGenerateCode,
        handleUnsubscribe,
        resetError
    }
}

export type DoctorTelegramNotificationsApi = ReturnType<typeof useTelegramNotifications>

/** Один экземпляр подписки Telegram для страницы профиля врача (provide из ProfileEdit) */
export const DoctorProfileTelegramNotificationsKey: InjectionKey<DoctorTelegramNotificationsApi> =
    Symbol('DoctorProfileTelegramNotifications')

